import React, { useCallback } from 'react';
import { SelectField } from '../../../../components/SelectField/SelectField';
import { TextField } from '../../../../components/TextField/TextField';
import { useCompleteUserInfoBusinessValidationForm } from '../../../../hooks/validations';
import { useLanguage } from '../../../../locale/useLanguage';
import citizenships from '../../../../assets/citizenships.json';
import { Citizeship } from '../../../../models';

type BusinessFormValidators = Omit<
  ReturnType<typeof useCompleteUserInfoBusinessValidationForm>,
  'handleSubmit'
>;

type BusinessFormProps = {
  validators: BusinessFormValidators;
  countries?: Array<string>;
  states?: Array<string>;
  cities?: Array<string>;
  postalCodes?: Array<string>;
};

export const BusinessForm: React.FC<BusinessFormProps> = ({
  validators,
  cities,
  states,
  postalCodes,
}) => {
  const [t] = useLanguage();

  const {
    companyNameValidator,
    errors,
    legalAddressValidator,
    legalRepresentativeValidator,
    taxCodeValidator,
    vatNumberValidator,
    companyCountryCodeValidator,
    companyMunicipalityValidator,
    companyPECValidator,
    companyProvinceValidator,
    companyMunicipalityFieldState,
    companySDICodeValidator,
    postcodeValidator,
    watchCompanyCountryCode,
  } = validators;

  const selectValueDisplayFn = useCallback((v: string) => v, []);

  const citizenshipSelectDisplayFn = useCallback(
    (c: Citizeship) => c.itName,
    []
  );
  const citizenshipSelectValueFn = useCallback(
    (c: Citizeship) => c.isoAlpha2,
    []
  );

  return (
    <>
      <div className="complete-profile--field-container">
        <TextField
          {...companyNameValidator}
          placeholder={t('completeUserInfoView.companyName')}
          error={errors.companyName?.message}
        />
      </div>
      <div className="complete-profile--field-container">
        <TextField
          {...taxCodeValidator}
          placeholder={t('completeUserInfoView.taxCode')}
          error={errors.fiscalCode?.message}
        />
      </div>
      <div className="complete-profile--field-container">
        <TextField
          {...legalRepresentativeValidator}
          placeholder={t('completeUserInfoView.legalRepresentative')}
          error={errors.legalRepresentative?.message}
        />
      </div>
      <div className="complete-profile--field-container">
        <TextField
          {...vatNumberValidator}
          placeholder={t('completeUserInfoView.vatNumber')}
          error={errors.vatNumber?.message}
        />
      </div>
      <div className="complete-profile--field-container">
        <TextField
          {...companyPECValidator}
          placeholder={t('completeUserInfoView.pec')}
          error={errors.companyPEC?.message}
        />
      </div>
      <div className="complete-profile--field-container">
        <TextField
          {...companySDICodeValidator}
          placeholder={t('completeUserInfoView.sdiCode')}
          error={errors.companySDICode?.message}
        />
      </div>
      <div className="complete-profile--billing-address">
        {t('completeUserInfoView.billingAddress')}:
      </div>
      <div className="complete-profile--field-container">
        <SelectField
          {...companyCountryCodeValidator}
          options={citizenships || []}
          placeholder={t('completeUserInfoView.state')}
          displayFn={citizenshipSelectDisplayFn}
          valueFn={citizenshipSelectValueFn}
          error={errors.companyCountryCode?.message}
        />
      </div>
      {watchCompanyCountryCode === 'IT' && (
        <div className="complete-profile--field-container">
          <SelectField
            {...companyProvinceValidator}
            options={states || []}
            placeholder={t('completeUserInfoView.province')}
            displayFn={selectValueDisplayFn}
            valueFn={selectValueDisplayFn}
            error={errors.companyProvince?.message}
          />
        </div>
      )}
      <div className="complete-profile--field-container">
        {watchCompanyCountryCode === 'IT' ? (
          <SelectField
            {...companyMunicipalityValidator}
            options={cities || []}
            placeholder={t('completeUserInfoView.municipality')}
            displayFn={selectValueDisplayFn}
            valueFn={selectValueDisplayFn}
            error={errors.companyMunicipality?.message}
          />
        ) : (
          <TextField
            value={companyMunicipalityValidator.value}
            name={companyMunicipalityValidator.name}
            onChange={companyMunicipalityValidator.onChange}
            placeholder={t('completeUserInfoView.municipality')}
            error={companyMunicipalityFieldState.error?.message}
          />
        )}
      </div>
      <div className="complete-profile--field-container">
        {watchCompanyCountryCode === 'IT' ? (
          <SelectField
            {...postcodeValidator}
            options={postalCodes || []}
            placeholder={t('completeUserInfoView.zipCode')}
            displayFn={selectValueDisplayFn}
            valueFn={selectValueDisplayFn}
            error={errors.companyPostcode?.message}
          />
        ) : (
          <TextField
            {...postcodeValidator}
            placeholder={t('completeUserInfoView.zipCode')}
            error={errors.companyPostcode?.message}
          />
        )}
      </div>
      <div className="complete-profile--field-container">
        <TextField
          {...legalAddressValidator}
          placeholder={t('completeUserInfoView.legalAddress')}
          error={errors.companyAddress?.message}
        />
      </div>
    </>
  );
};
